import GiftCardCWHCPrint from "./giftCardCWHCPrinter";
import { traceGiftCardLines, traceGiftCardSkipped } from "./giftCardPrintType";
import Axios from "axios";
import env from "../../config/env";
import CWOpenDrawer from "./CWOpenDrawer";
import printToken from "./printTokens";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import { processReceiptTemplate, replacePlaceholders, getDeepValue, formatValue } from "./templateProcessor";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useTransactionStore from "../../stores/useTransactionStore";
import useRestaurantStore from "../../stores/useRestaurantStore";
import { getIsInterState } from "../../utils/taxUtils";
import getReceiptCopies from "./receiptCopies";
import { notifyTemplateNotConfigured, notifyTemplateInvalid, PRINT_TEMPLATES } from "./printTemplateErrors";

// The local hardwareController print bridge acks the HTTP POST as soon as it accepts the job, not
// once the printer has actually finished writing it to paper — so even chaining the next request on
// the previous one's resolved promise gives the physical printer zero real gap between jobs, and only
// one copy comes out even though every request reports success. Space copies out with a real delay
// (same 1000ms gap already proven to work for the window.print() loop in htmlPrinter.js).
const PRINT_COPY_DELAY_MS = 1000;
// BUG: posConfig copy-count fields (sales/return/layaway/loyalty_receipt_copies) have shipped as
// non-numeric values (e.g. the string "N") for some stores. `copyIndex >= totalCopies` silently
// coerces a non-numeric totalCopies to NaN, and any comparison against NaN is always false, so the
// loop below never terminates — it POSTs a new "copy" to the printer bridge every second forever.
// Normalize here as a backstop so this can never infinite-loop again regardless of what a caller
// (or a misconfigured till) passes in. Missing/non-numeric values (e.g. "N") fall back to 1 copy
// rather than 0, so a bad config value still prints once instead of silently printing nothing.
// An explicit 0 is preserved as-is (a store intentionally disabling that receipt type).
const toSafeCopyCount = (value) => {
  if (value === null || value === undefined || value === "") return 1;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 1;
};
export const printCopiesToBridge = (url, data, headers, totalCopies, onCopySuccess, onCopyError) => {
  const safeTotalCopies = toSafeCopyCount(totalCopies);
  const sendCopy = (copyIndex) => {
    if (copyIndex >= safeTotalCopies) return;
    Axios.post(url, data, { headers })
      .then(() => {
        onCopySuccess && onCopySuccess(copyIndex);
        setTimeout(() => sendCopy(copyIndex + 1), PRINT_COPY_DELAY_MS);
      })
      .catch((error) => {
        onCopyError && onCopyError(copyIndex, error);
        setTimeout(() => sendCopy(copyIndex + 1), PRINT_COPY_DELAY_MS);
      });
  };
  sendCopy(0);
};

const CWHCPrint = (printData, PStatus) => {
  console.debug("[CWHCPrint] entry", { sOrderID: printData?.sOrderID, PStatus });
  let receiptData = { ...printData };
  // BUG-2227: a receipt reprint (completion screen "Print Receipt", Sales History reprint,
  // preorder reprint) routes back through here with PStatus "Duplicate Copy" / isReprint set.
  // The KOT-fire block below only guarded on item.order !== "Y", so on a straight quick-sale /
  // takeaway checkout (items never stamped order:"Y") the reprint re-fired every KOT — posting
  // a second ticket to the printer bridge AND calling upsertPOSKOT again, which mints a fresh
  // cwrFbKotId and shows the order a second time in KDS even though only one sOrder exists.
  // A reprint must never fire a KOT; only the fresh checkout auto-print (PrintController, no
  // PStatus) does.
  const isReprintCopy = PStatus === "Duplicate Copy" || printData?.isReprint === true;
  const posConfig = usePOSConfigStore.getState().posConfig;
  const tillData = useAuthStore.getState().tillData;
  const pricePrecision = tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision;
  receiptData.time = receiptData.orderTime ? receiptData.orderTime.split(" ").pop() : "";

  receiptData.items = (receiptData.items || []).filter((item) => {
    return !(item.nettotal === 0 && item.unitTax === 0 && item.netStd === 0 && item.type !== "S" && item?.itemType !== "TGD");
  });
  receiptData.items = (receiptData.items || []).filter((item) => {
    return item?.orderStatus !== "CL";
  });
  receiptData.items = (receiptData.items || []).filter((item) => {
    return item?.productSegment !== "BG";
  });
  receiptData.items = (receiptData.items || []).map((item, index) => {
    item.sno = index + 1;
    // TSK-3524: cart items carry add-ons under selectedAddons (see cart.js, useProductAddons.js);
    // item.addons only ever holds pre-normalized data (order-history/aggregator reprints).
    // Reading item.addons alone silently dropped add-ons for every fresh checkout print, same
    // fallback CWKotPrinter.js and consolidateBillItems.js already apply for this reason.
    const rawAddons = item.selectedAddons?.length > 0 ? item.selectedAddons : item.addons;
    item.addons = (rawAddons || []).map((addon) => ({
      name: "+ " + addon?.name,
      qty: addon?.qty ?? addon?.weight,
      mrp: addon?.price,
      amount: (addon?.qty ?? addon?.weight ?? 0) * (addon?.price || 0),
    }));

    return item;
  });

  // BUG-2301: cash-denomination round-off (overPayedAmount) is a front-of-house
  // tendering aid. Only the receipt's total/totalStd fold-in is gated on the
  // currency's own roundoffForPos (cs_currency.roundoffForPos, currencies[0].roundoffForPos
  // -- the same key TSK-3996 added for getRoundingMethod): an explicit "N" means that
  // currency wants no rounding folded into the printed total, so it reflects the real
  // order amount (the actual cash tendered/change is still available separately via
  // payment.amountTendered / receiptData.change). "Y"/unset keeps the original behavior
  // of folding the round-off into the total.
  // The per-payment-line fold-in below is unrelated and always governed solely by that
  // payment's own roundOffForPos ("Y"/integratedPayment false) -- the currency flag must
  // never gate it (see TIC-9968: currency and payment-method roundOffForPos are independent
  // keys and must not be cross-gated).
  // BUG-2324/TIC-10229: overPayedAmount is always a non-negative magnitude (how far the
  // rounded amount is from the true amount, e.g. Math.ceil(73.69/0.25)*0.25 - 73.69 = 0.06).
  // Adding it unconditionally only rounds correctly for a positive (sale) amount, moving it
  // further from zero (73.69 -> 73.75). A return's amount is negative (-73.69) -- adding the
  // same positive delta moves it TOWARD zero instead (-73.69 + 0.06 = -73.63), which is not a
  // rounded denomination at all, just an arbitrary smaller refund. Rounding a return the same
  // way a sale rounds means increasing its MAGNITUDE, i.e. subtracting the delta when the
  // amount is negative (-73.69 - 0.06 = -73.75) -- confirmed against the reported receipt
  // (total -73.69, denomination 0.25, printed Cash -73.63 pre-fix).
  const applyRoundOffDelta = (value, delta) => (value < 0 ? value - delta : value + delta);

  const currencyRoundOffForPos = tillData?.tillAccess?.csBunit?.currencies?.[0]?.roundoffForPos;
  if (currencyRoundOffForPos === "N") {
    receiptData.total = Number(Number(receiptData.total).toFixed(pricePrecision ?? 2));
    receiptData.totalStd = Number(Number(receiptData.totalStd).toFixed(pricePrecision ?? 2));
  } else {
    receiptData.total = Number(applyRoundOffDelta(receiptData.total, Number(receiptData.overPayedAmount || 0)).toFixed(pricePrecision ?? 2));
    receiptData.totalStd = Number(applyRoundOffDelta(receiptData.totalStd, Number(receiptData.overPayedAmount || 0)).toFixed(pricePrecision ?? 2));
  }

  const overPayed = Number(receiptData.overPayedAmount || 0);
  receiptData.payments = receiptData?.payments?.map((payment) => {
    let amount = Number(payment.amount);
    if (payment.roundOffForPos === "Y" && payment.integratedPayment === false) {
      amount = Number(applyRoundOffDelta(amount, overPayed).toFixed(pricePrecision ?? 2));
    } else {
      amount = Number(amount.toFixed(pricePrecision ?? 2));
    }
    return { ...payment, amount };
  });

  const formatExactDateTime = (dateStr) => {
    const [datePart, timePart] = dateStr.split(" ");
    const [day, month, year] = datePart.split("-");
    let [hours, minutes, seconds] = timePart.split(":");

    hours = Number(hours);
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12; // convert 0 -> 12

    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  if (receiptData?.deliveryDate) {
    receiptData.deliveryDate = formatExactDateTime(receiptData?.deliveryDate);
  }

  const currentDate = new Date().toISOString().split("T")[0];
  const storedTokenData = useRestaurantStore.getState().tokenData || {};
  const lastDate = storedTokenData.date || "";
  let tokenCounter = storedTokenData[lastDate] || 0;

  if (lastDate !== currentDate) {
    tokenCounter = 0; // Reset token counter if date changes
  }

  // Initialize vendors as an array
  const vendors = [];

  // Group items by productionCenter only for items with a valid productionCenterId.
  // Skip already-fired items (order === "Y") so completing a bill doesn't re-fire KOT.
  const productionCenters = {};
  let hasProductionCenter = false;
  (receiptData.items || []).forEach((item) => {
    const productionCenter = item.productionCenter || "Unknown P C";
    const productionCenterId = item.productionCenterId || "";
    const printerId = item?.printerId || "";

    if (productionCenterId && !receiptData?.externalId && item.order !== "Y") {
      hasProductionCenter = true;

      if (!productionCenters[productionCenter]) {
        tokenCounter++;
        const newToken = `T-${tokenCounter}`;

        productionCenters[productionCenter] = {
          productionCenter,
          productionCenterId,
          token: newToken,
          items: [],
          subTotal: 0,
          printerId,
        };

        vendors.push(productionCenters[productionCenter]);
      }

      productionCenters[productionCenter].items.push({
        name: item.name || "",
        weight: item.weight || 1,
        grossunit: parseFloat(item.grossunit || item.salePrice || 0).toFixed(2),
        linegross: parseFloat(item.linegross || item.nettotal || 0).toFixed(2),
        addons: item.selectedAddons || item.addons || [],
        preparationTime: item?.preparationTime || null,
        notes: item?.notes || null,
      });

      productionCenters[productionCenter].subTotal += parseFloat(item.linegross || 0);
    }
  });

  // BUG-1971: aggregator KOT printing used to be fired from here too, independent of
  // and in addition to the explicit CWKOTPrinter call the accept-flow callers
  // (salesHistoryUtils.js, Aggregator/index.js) now always make when
  // disableAggregatorKOTPrint is off. Since this function only runs for one specific
  // print-routing choice (till's cwController flag), keeping it here meant aggregator
  // KOT printing depended on hardware-controller routing (worked on some tills, silently
  // never fired on others), and doubled up on tills where it did fire. The KOT decision
  // now lives solely at the accept-flow call sites, independent of print routing.

  // Case 2: Production Centers → Multiple Prints (1 per PC)
  // else {
  //   vendors.forEach((vendor) => {
  //     CWKOTPrinter(vendor, false);
  //   });
  // }

  // Store updated token data only if at least one item was processed (i.e., vendors exist)
  if (vendors.length > 0) {
    storedTokenData.date = currentDate;
    storedTokenData[currentDate] = tokenCounter; // Update for the current date
    useRestaurantStore.getState().setTokenData(storedTokenData);
  }
  receiptData.vendorsCount = vendors.length.toString();
  // Add vendors to receiptData
  receiptData.vendors = vendors;
  receiptData.token = `T-${tokenCounter}`;
  // Set orderDate from cart data or current date
  receiptData.orderDate = printData.orderDate || new Date().toISOString().split("T")[0];

  if (Array.isArray(receiptData.items)) {
    receiptData.items = receiptData.items.map((item, index) => ({
      ...item,
      index,
      ediCode: Array.isArray(item.uomData) && item.uomData.length > 0 ? item.uomData[0]?.ediCode || "" : "",
    }));
  }

  // Print KOT only if there are vendors (i.e., items with valid production centers).
  // BUG-2227: never on a reprint — see isReprintCopy note at the top of this function.
  if (!isReprintCopy && posConfig?.enableKOTPrint === "Y" && receiptData?.saleType?.enableKot === "Y" && vendors.length > 0 && tillData?.tillAccess?.cwrTill?.kotPrintTemplate?.xmlcode2) {
    // Call tokens.js for each vendor with the same bill token
    vendors.forEach((vendor) => {
      printToken(vendor, receiptData);
    });
  } else if (!isReprintCopy && posConfig?.enableKOTPrint === "Y" && receiptData?.saleType?.enableKot === "Y" && vendors.length > 0) {
    notifyTemplateNotConfigured(PRINT_TEMPLATES.kot, "KOT");
  }

  // Coerced via toSafeCopyCount — posConfig has shipped non-numeric values here (e.g. "N") for some
  // stores, which previously passed through as truthy strings and broke the copy-loop's termination
  // check in printCopiesToBridge (see comment there). A missing/non-numeric value now safely means
  // "1 copy" instead of "infinite copies"; an explicit 0 still means "don't print this receipt type".
  const sales_receipt_copies = toSafeCopyCount(posConfig?.sales_receipt_copies);
  const return_receipt_copies = toSafeCopyCount(posConfig?.return_receipt_copies);
  const layaway_receipt_copies = toSafeCopyCount(posConfig?.layaway_receipt_copies);
  const layaway_paid_receipt_copies = toSafeCopyCount(posConfig?.layaway_paid_receipt_copies);
  const loyalty_receipt_copies = toSafeCopyCount(posConfig?.loyalty_receipt_copies);
  const printerURL = tillData.tillAccess.cwrTill.hardwareController.imageUrl;
  let isloyalty =
    (receiptData.payments && receiptData.payments.some((payment) => payment.isloyalty)) ||
    (receiptData?.paymentData && receiptData?.paymentData.some((payment) => payment?.isloyalty));
  const templateId = useTransactionStore.getState().selectedPrintTemplateId || null;
  let docTypeTemplateId = null;
  let printTemplate = null;
  if (templateId) {
    // Check server templates first
    let printTemplates = [];
    try {
      if (tillData?.tillAccess?.cwrTill?.printTemplates) printTemplates = JSON.parse(tillData.tillAccess.cwrTill.printTemplates);
    } catch {
      /* ignore */
    }
    printTemplate = printTemplates?.find((template) => template?.cwr_printtemplate_id === templateId)?.xmlcode2;
    docTypeTemplateId = printTemplates?.find((template) => template?.cwr_printtemplate_id === templateId)?.cs_doctype_template_id;
    // Fallback: check locally designed templates (from Template Designer)
    if (!printTemplate) {
      try {
        const localTemplates = JSON.parse(localStorage.getItem("cwPrintTemplates") || "[]");
        const localMatch = localTemplates.find((t) => t.cwr_printtemplate_id === templateId);
        if (localMatch) {
          printTemplate = localMatch.xmlcode2 || JSON.stringify({ elements: localMatch.elements || [] });
        }
      } catch {
        /* ignore parse errors */
      }
    }
  }
  if (docTypeTemplateId !== null) {
    const isDuplicate = PStatus === "Duplicate Copy";
    // Was hardcoded to sales_receipt_copies regardless of receipt type, so returns/layaway/loyalty
    // receipts printed via a selected custom print template ignored their own copies configuration.
    // F-10: getReceiptCopies() returns posConfig's copy-count value un-normalized (e.g. the string
    // "2") when it's a valid positive value -- only its own non-numeric/missing fallback path
    // returns a real number. printCopiesToBridge below already normalizes internally via
    // toSafeCopyCount, so the right number of copies still gets sent either way, but the
    // `successfulPrints === printCount` check further down compares a plain number against
    // whatever type this is -- 2 === "2" is false, so the cash drawer never opens and DIG
    // gift-card/credit-note receipts never print. Normalize here with the same helper.
    const printCount = isDuplicate ? 1 : toSafeCopyCount(getReceiptCopies(receiptData, posConfig));
    console.debug("[CWHCPrint] docTypeTemplateId branch dispatch", {
      sOrderID: receiptData?.sOrderID,
      isReturn: receiptData?.isReturn,
      layAway: receiptData?.layAway,
      paid: receiptData?.paid,
      PStatus,
      isDuplicate,
      sales_receipt_copies,
      return_receipt_copies,
      printCount,
    });
    const formData = new FormData();
    formData.append("windowId", "7004");
    formData.append("tabId", "270EED9D0E7F4C43B227FEDC44C5858F");
    formData.append("recordId", receiptData?.sOrderID || "");
    formData.append("type", "Print-Template");
    formData.append("printTemplateId", docTypeTemplateId);

    const { access_token } = getOAuthHeaders() || {};

    Axios.post(env.downloadPrintTemplatesUrl, formData, {
      headers: {
        Authorization: access_token,
      },
    })
      .then((res) => {
        const fileUrl = res?.data;

        if (!fileUrl) return;

        // F-11: onCopyError below used to only log, never advancing this counter -- one
        // transient bridge failure meant successfulPrints could never reach printCount,
        // permanently skipping the drawer-open/gift-card block for that sale even though
        // the remaining copies printed fine. Count attempts (success or failure) instead.
        // BUG (post-F-11 regression): attemptsCompleted alone can't distinguish "every copy
        // printed" from "every copy failed" -- track real successes too and require at least
        // one before opening the drawer or issuing a gift card/credit note.
        let attemptsCompleted = 0;
        let successfulPrints = 0;
        const onCopyAttemptDone = (didSucceed) => {
          attemptsCompleted++;
          if (didSucceed) successfulPrints++;
          if (attemptsCompleted === printCount) {
            if (successfulPrints === 0) traceGiftCardSkipped("CWHC-copy#1");
            if (successfulPrints === 0) return;
            if (receiptData.layAway === "N" && !PStatus && !receiptData.draftFlag && receiptData?.payments?.some((p) => p?.name === "Cash")) {
              CWOpenDrawer();
            }

            traceGiftCardLines("CWHC#1", receiptData);
            receiptData.items.forEach((data) => {
              if (receiptData.isReturn && data?.giftCardType === "DIG") {
                GiftCardCWHCPrint(data, "Credit Note");
              } else if (data?.giftCardType === "DIG") {
                GiftCardCWHCPrint(data, "Gift Card");
              }
            });
          }
        };

        printCopiesToBridge(
          `${printerURL}printer`,
          fileUrl,
          { "Content-Type": "text/plain" },
          printCount,
          () => {
            onCopyAttemptDone(true);
          },
          (i, err) => {
            console.error("Print failed!", err);
            onCopyAttemptDone(false);
          }
        );
        useTransactionStore.getState().setSelectedPrintTemplateId(null);
      })
      .catch((err) => {
        console.error("Error in print flow:", err);
      });
    useTransactionStore.getState().setSelectedPrintTemplateId(null);
    return;
  }
  let receiptTemplate = {};
  try {
    if (printTemplate) {
      receiptTemplate = JSON.parse(printTemplate);
    } else if (tillData?.tillAccess?.cwrTill?.printTemplate?.xmlcode2) {
      receiptTemplate = JSON.parse(tillData.tillAccess.cwrTill.printTemplate.xmlcode2);
    } else {
      notifyTemplateNotConfigured(PRINT_TEMPLATES.sales, "Receipt");
    }
  } catch (e) {
    console.error("[CWHCPrint] Failed to parse receipt template:", e.message);
    notifyTemplateInvalid(PRINT_TEMPLATES.sales, "Receipt");
  }
  // TSK-2235 updated processReceiptTemplate to only handle loop extras in the new
  // "tableRow + dataMapping" format, removing the old "subField + column" handler and
  // its return [] fallback. Any loop extra not in the new format causes the flatMap
  // callback to return undefined, which crashes Object.values(row) in the filter.
  // Normalize here (on the freshly-parsed template copy) before the template processor
  // sees it, preserving correct output for all template styles.
  if (receiptTemplate.elements) {
    receiptTemplate.elements.forEach((element) => {
      (element.extraRows || []).forEach((extra, idx) => {
        if (extra.loop && !(extra.type === "tableRow" && extra.dataMapping)) {
          if (extra.subField && extra.column) {
            // Old-style: map the single subField value into its target column
            element.extraRows[idx] = { ...extra, type: "tableRow", dataMapping: { [extra.column]: extra.subField } };
          } else {
            // Unrecognised loop shape — disable loop so the flatMap is never entered
            element.extraRows[idx] = { ...extra, loop: false };
          }
        }
      });
    });
  }
  const isInterState = getIsInterState(receiptData);
  const groupedItems = {};
  (receiptData.items || []).forEach((item) => {
    const key = item.tax && item.tax.rate !== undefined && item.tax.rate !== null ? item.tax.rate : item.taxRate || "0";
    if (!groupedItems[key]) {
      groupedItems[key] = [];
    }
    groupedItems[key].push(item);
  });

  const groupedItemsByTaxRate = [];
  for (const taxRate in groupedItems) {
    const totalTaxAmount = groupedItems[taxRate].reduce(
      (total, item) =>
        total +
        (item.tax && item.tax.rate !== undefined && item.tax.rate !== null
          ? parseFloat(parseFloat(item.linetax || 0).toFixed(2))
          : parseFloat(parseFloat(item.taxAmount || 0).toFixed(2))),
      0
    );
    const totalNetTotal = groupedItems[taxRate].reduce(
      (total, item) =>
        total +
        (item.tax && item.tax.rate !== undefined && item.tax.rate !== null
          ? parseFloat(parseFloat(item.linegross || 0).toFixed(2))
          : parseFloat(parseFloat(item.nettotal || 0).toFixed(2))),
      0
    );
    const totalSgstAmount = isInterState
      ? 0
      : groupedItems[taxRate].reduce((total, item) => total + parseFloat(parseFloat(item.sgst || item.tax?.sgst || (item.taxAmount || 0) / 2).toFixed(2)), 0);
    const totalCgstAmount = isInterState
      ? 0
      : groupedItems[taxRate].reduce((total, item) => total + parseFloat(parseFloat(item.cgst || item.tax?.cgst || (item.taxAmount || 0) / 2).toFixed(2)), 0);
    groupedItemsByTaxRate?.push({
      taxRate: taxRate,
      totalLinenet: (totalNetTotal - totalTaxAmount).toFixed(2),
      totalLinetax: totalTaxAmount.toFixed(2),
      totalLinegross: totalNetTotal.toFixed(2),
      totalSgst: totalSgstAmount.toFixed(2),
      totalCgst: totalCgstAmount.toFixed(2),
      totalIgst: isInterState ? totalTaxAmount.toFixed(2) : "0.00",
    });
  }

  // Extract values from customer.metaData
  const GST_META_KEYS = ["Gst No", "G ST No", "GST No", "GST Number", "gstNo"];
  let gstNo = "";
  if (receiptData?.customer?.metaData && Array.isArray(receiptData.customer.metaData)) {
    const gstObj = receiptData.customer.metaData.find((item) => GST_META_KEYS.includes(item.key));
    // Fall back to taxId — some store configs store GST in the taxId field
    gstNo = gstObj?.value || receiptData.customer?.taxId || "";
  }

  let legalName = "";
  if (receiptData?.customer?.metaData && Array.isArray(receiptData.customer.metaData)) {
    const legalNameObj = receiptData.customer.metaData.find((item) => item.key === "Company Name");
    legalName = legalNameObj?.value || receiptData.customer?.legalName || "";
  }

  // Use Zustand — same source as useOrderProcessing.js (localStorage is cleared after order save)
  const isB2b = useTransactionStore.getState().isB2bEnabled === "true";

  let addressLine1 = "";
  if (receiptData?.customer?.metaData && Array.isArray(receiptData.customer.metaData)) {
    const b2bFlag = localStorage.getItem("isB2bEnabled") === "true";
    let customerConfig = [];
    try {
      customerConfig = JSON.parse(localStorage.getItem("customerConfigs")) || [];
    } catch {
      /* ignore */
    }
    let customerConfigData = {};
    try {
      if (customerConfig[0]?.configuration) customerConfigData = JSON.parse(customerConfig[0].configuration);
    } catch {
      /* ignore */
    }
    const retailCustomerGroups = customerConfigData?.fieldGroups || [];
    if (b2bFlag && retailCustomerGroups.length > 0) {
      const groupId = retailCustomerGroups?.find((g) => g.name === "Invoicing Address")?.id;
      const rawVal = groupId && receiptData.customer.metaData?.find((item) => item.key === groupId)?.value;
      const addressObj = rawVal ? JSON.parse(rawVal) : null;
      addressLine1 = addressObj?.line1 || "";
    } else if (retailCustomerGroups.length > 0) {
      const groupId = retailCustomerGroups?.find((g) => g.name === "Address")?.id;
      const rawVal2 = groupId && receiptData.customer.metaData?.find((item) => item.key === groupId)?.value;
      const addressObj = rawVal2 ? JSON.parse(rawVal2) : null;
      addressLine1 = addressObj?.line1 || "";
    } else {
      const addressObj = receiptData.customer.metaData?.find((item) => item.key === "Line1");
      addressLine1 = addressObj?.value || "";
    }
  }

  let state = "";
  if (receiptData?.customer?.metaData && Array.isArray(receiptData.customer.metaData)) {
    const stateObj = receiptData.customer.metaData.find((item) => item.key === "State");
    const stateId = stateObj?.value || "";
    const statesData = JSON.parse(localStorage.getItem("statesData")) || [];
    state = statesData.find((item) => item.RecordID === stateId)?.Name || "";
  }

  let country = "";
  if (receiptData?.customer?.metaData && Array.isArray(receiptData.customer.metaData)) {
    const countryObj = receiptData.customer.metaData.find((item) => item.key === "Country");
    const countryId = countryObj?.value || "";
    const countriesData = JSON.parse(localStorage.getItem("countriesData")) || [];
    country = countriesData.find((item) => item.RecordID === countryId)?.Name || "";
  }

  receiptData.customer = {
    ...receiptData.customer,
    gstNo: gstNo,
    legalName: legalName,
    addressLine1: addressLine1,
    country: country,
    state: state,
  };
  // Cashier flow pre-computes groupedItemsByTaxRate as an array; mobile/WebSocket flow sends {} so we fall back to the locally computed one
  const resolvedGroupedItemsByTaxRate =
    Array.isArray(receiptData?.groupedItemsByTaxRate) && receiptData.groupedItemsByTaxRate.length > 0 ? receiptData.groupedItemsByTaxRate : groupedItemsByTaxRate;
  const totalCgst = resolvedGroupedItemsByTaxRate.reduce((sum, row) => sum + Number(row.totalCgst || 0), 0);
  const totalSgst = resolvedGroupedItemsByTaxRate.reduce((sum, row) => sum + Number(row.totalSgst || 0), 0);
  const totalIgst = resolvedGroupedItemsByTaxRate.reduce((sum, row) => sum + Number(row.totalIgst || 0), 0);

  receiptData.totalQty = (receiptData.items || []).filter((item) => item.isCanceled !== "Y").reduce((sum, item) => sum + (Number(item.weight) || 1), 0);
  receiptData.itemCount = (receiptData.items || []).filter((item) => item.isCanceled !== "Y").length;

  // Mobile app sends itemsSummery: {} — compute it from items so templates using itemsSummery.nonDecimalItems.totalQty resolve correctly
  if (!receiptData.itemsSummery || Object.keys(receiptData.itemsSummery).length === 0) {
    const activeItems = (receiptData.items || []).filter((item) => item.isCanceled !== "Y");
    const nonDecItems = activeItems.filter((item) => !item.isDecimalQty);
    const decItems = activeItems.filter((item) => item.isDecimalQty);
    receiptData.itemsSummery = {
      nonDecimalItems: {
        totalQty: nonDecItems.reduce((sum, item) => sum + (Number(item.weight) || 1), 0),
        totalAmount: nonDecItems.reduce((sum, item) => sum + (Number(item.nettotal) || 0), 0),
      },
      decimalItems: {
        totalQty: decItems.reduce((sum, item) => sum + (Number(item.weight) || 0), 0),
        totalAmount: decItems.reduce((sum, item) => sum + (Number(item.nettotal) || 0), 0),
      },
      itemsQty: activeItems.reduce((sum, item) => sum + (Number(item.weight) || 1), 0),
      items: activeItems.length,
    };
  }

  // Merge groupedItemsByTaxRate and PStatus into receiptData.
  // Items are shallow-copied so that salePrice is restored to the original catalog price
  // (realPrice) for receipt display. Pricing rules intentionally set salePrice to the
  // post-discount effective price; realPrice is never modified and always holds the
  // original price. Templates should reference realPrice or salePrice — both now equal
  // the original price in this receipt-scoped copy.
  // grossunit/linegross/netunit/linenet are the Price/Amount fields different sales-receipt
  // templates bind to (processReceiptTemplate does a direct property read with no fallback,
  // unlike CWKotPrinter/generateKOTs which already fall back to salePrice/nettotal). Some
  // tills' templates map Price/Amount to netunit/linenet instead of grossunit/linegross.
  // Order-history and aggregator-reconstructed items can arrive without any of these set,
  // leaving the printed Price/Amount columns blank even though KOT prints fine — apply the
  // same fallback here so the sales receipt gets the same safety net as KOT.
  //
  // BUG-1558: item.grossunit is a cart/business-logic field (set by cart.js,
  // useCartOperations.js, enrichLineItemFields, etc.) that intentionally holds the POST-DISCOUNT
  // unit price — it's synced to the backend and read for revenue reporting (e.g.
  // Dashboard2), so it must not be mutated to mean something else. realPrice/grossstd are
  // the pre-discount standard-price counterparts of grossunit and must take priority
  // in this print-only copy so the receipt's Price column shows the original price while
  // Amount/Sub Total/grossTotal below still reflect the actual discounted sale.
  const receiptItems = (receiptData.items || []).map((item) => {
    const grossunit = item.realPrice || item.grossstd || item.grossunit || item.salePrice || 0;
    const linegross = item.linegross || item.nettotal || 0;
    // BUG-1909: netunit/linenet is a separate tax-EXCLUSIVE Price/Amount pair some
    // templates bind to (see template.txt-style dataMapping "price": "netunit",
    // "amount": "linenet"), independent of the tax-inclusive grossunit/linegross pair
    // above. d41179cf originally read `item.netunit || grossunit` here specifically for
    // this pairing; 1debf594 (chasing the unrelated BUG-1558 rounding issue) collapsed it
    // to unconditional `grossunit` while editing the same line, which is what broke this —
    // item.netunit/item.linenet already hold the correct tax-exclusive values for live cart
    // items (cart.js, useCartOperations.js), and item.salePrice already holds the correct
    // tax-exclusive value for order-history/aggregator-reconstructed items
    // (salesHistoryUtils). grossunit is kept as the final-tier fallback so items lacking both
    // (if any exist) still print a non-zero Price rather than reintroducing the "still blank"
    // regression d41179cf fixed. Deriving linenet from netunit*qty when missing keeps
    // Amount = Price x Qty instead of drifting from an independently rounded
    // linegross - taxAmount.
    // F-34: use ?? not || — 0 is a legitimate value for a free/100%-discounted/loyalty-
    // redeemed line, and || treats that correct 0 the same as missing data, silently
    // falling through to a non-zero price/amount and inflating the printed Subtotal.
    const netunit = item.netunit ?? item.salePrice ?? grossunit;
    return {
      ...item,
      salePrice: item.realPrice ?? item.salePrice,
      grossunit,
      linegross,
      netunit,
      linenet: item.linenet ?? parseFloat((netunit * (item.weight || 0)).toFixed(pricePrecision ?? 2)),
    };
  });

  // Always recompute subTotal from active (non-cancelled) items rather than trusting cart.subTotal,
  // which can go stale (e.g. cancel-then-re-add without a fresh sync, BUG-1504).
  //
  // Sourced from receiptItems' linenet (the same corrected, tax-exclusive value the Amount
  // column above prints) rather than raw nettotal - taxAmount (linegross - linetax): those are
  // two independently-rounded backend fields, and summing their per-line difference across
  // multiple items can drift a few paisa from the true tax-exclusive subtotal even though each
  // individual Amount is correct, leaving Subtotal + tax + packing != the (correctly-sourced)
  // printed Total.
  if (receiptItems.length > 0) {
    const activeReceiptItems = receiptItems.filter((item) => item.isCanceled !== "Y");
    receiptData.subTotal = parseFloat(activeReceiptItems.reduce((sum, item) => sum + (Number(item.linenet) || 0), 0).toFixed(pricePrecision ?? 2));
  }

  const updatedReceiptData = {
    ...receiptData,
    items: receiptItems,
    groupedItemsByTaxRate: resolvedGroupedItemsByTaxRate,
    totalCgst,
    totalSgst,
    totalIgst,
    PStatus,
    isB2b,
    previousCashBalance: receiptData.customer?.retlLoyaltyBalance + receiptData?.redemptionPoints,
    discountInfo: receiptData.discountInfo || [],
    grossTotal: parseFloat((receiptData.total + (receiptData.discount || 0)).toFixed(pricePrecision ?? 2)),
  };
  updatedReceiptData.isPreOrderPage = window.location.pathname.includes("/preorder") || receiptData.isPreOrderPage === true;

  const processedReceipt = processReceiptTemplate(receiptTemplate, updatedReceiptData);

  // Embed transaction metadata so CW Browser printInterceptor can archive with correct values
  processedReceipt.documentNo = receiptData.documentno || null;
  processedReceipt.receiptType =
    receiptData.isReturn === "Y" || receiptData.isReturn === true ? "return" : receiptData.layAway === "Y" ? "layaway" : "sale";
  processedReceipt.storeId = tillData?.tillAccess?.csBunit?.csBunitId || null;
  processedReceipt.terminalId = tillData?.tillAccess?.cwrTill?.till || null;
  processedReceipt.totalQty = updatedReceiptData.totalQty ?? null;
  processedReceipt.itemCount = updatedReceiptData.itemCount ?? null;
  processedReceipt.grossAmount = updatedReceiptData.grossTotal ?? null;
  processedReceipt.discountAmount = receiptData.discount ?? null;
  processedReceipt.taxAmount = receiptData.tax ?? null;
  processedReceipt.netAmount = receiptData.total ?? null;
  processedReceipt.paymentSummary = Array.isArray(receiptData.payments)
    ? parseFloat(receiptData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0).toFixed(pricePrecision ?? 2))
    : null;

  let printReciptData = receiptData;
  let decodedData = JSON.stringify(processedReceipt, null, 2);
  console.debug("[CWHCPrint] dispatch check", {
    sOrderID: receiptData?.sOrderID,
    printerURL,
    itemsLength: printReciptData.items?.length,
    draftFlag: printReciptData.draftFlag,
    isReturn: printReciptData.isReturn,
    layAway: printReciptData.layAway,
    sales_receipt_copies,
    return_receipt_copies,
    PStatus,
    isloyalty,
  });
  if (
    printReciptData.items.length > 0 &&
    !printReciptData.draftFlag &&
    printReciptData.isReturn &&
    (printReciptData.layAway === "N" || !printReciptData.layAway) &&
    return_receipt_copies !== 0 &&
    !PStatus &&
    !isloyalty
  ) {
    // F-11: count attempts (success or failure), not just successes -- see comment on the
    // docTypeTemplateId branch above for why a single failed copy previously blocked this
    // block from ever firing. Requires at least one real success before firing (see same
    // comment for the post-F-11 all-failed regression this guards against).
    let attemptsCompleted = 0;
    let successfulPrints = 0;
    const onCopyAttemptDone = (didSucceed) => {
      attemptsCompleted++;
      if (didSucceed) successfulPrints++;
      if (attemptsCompleted === return_receipt_copies) {
        if (successfulPrints === 0) traceGiftCardSkipped("CWHC-copy#2");
        if (successfulPrints === 0) return;
        if (printReciptData.layAway === "N" && !PStatus && !printReciptData.draftFlag && printReciptData?.payments?.some((payment) => payment?.name === "Cash")) {
          CWOpenDrawer();
        }
        traceGiftCardLines("CWHC#2", printReciptData);
        printReciptData.items.forEach((data) => {
          if (printReciptData.isReturn && data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Credit Note");
          } else if (data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Gift Card");
          }
        });
      }
    };
    console.debug("[CWHCPrint] sending return receipt to printerURL", { sOrderID: receiptData?.sOrderID, printerURL, return_receipt_copies });
    printCopiesToBridge(
      `${printerURL}printer`,
      decodedData,
      { "Content-Type": "text/plain" },
      return_receipt_copies,
      (i) => {
        console.debug("[CWHCPrint] return printerURL post succeeded", { sOrderID: receiptData?.sOrderID, copyIndex: i });
        onCopyAttemptDone(true);
      },
      (i, response) => {
        console.error("[CWHCPrint] return printerURL post FAILED", { sOrderID: receiptData?.sOrderID, printerURL, copyIndex: i, error: response });
        onCopyAttemptDone(false);
      }
    );
  } else if (
    printReciptData.items.length > 0 &&
    !printReciptData.draftFlag &&
    !printReciptData.isReturn &&
    (printReciptData.layAway === "N" || !printReciptData.layAway) &&
    sales_receipt_copies !== 0 &&
    !PStatus &&
    !isloyalty
  ) {
    // F-11: count attempts (success or failure), not just successes -- see comment on the
    // docTypeTemplateId branch above for why a single failed copy previously blocked this
    // block from ever firing. Requires at least one real success before firing (see same
    // comment for the post-F-11 all-failed regression this guards against).
    let attemptsCompleted = 0;
    let successfulPrints = 0;
    const onCopyAttemptDone = (didSucceed) => {
      attemptsCompleted++;
      if (didSucceed) successfulPrints++;
      if (attemptsCompleted === sales_receipt_copies) {
        if (successfulPrints === 0) traceGiftCardSkipped("CWHC-copy#3");
        if (successfulPrints === 0) return;
        if (printReciptData.layAway === "N" && !PStatus && !printReciptData.draftFlag && printReciptData?.payments?.some((payment) => payment?.name === "Cash")) {
          CWOpenDrawer();
        }
        traceGiftCardLines("CWHC#3", printReciptData);
        printReciptData.items.forEach((data) => {
          if (printReciptData.isReturn && data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Credit Note");
          } else if (data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Gift Card");
          }
        });
      }
    };
    console.debug("[CWHCPrint] sending sales receipt to printerURL", { sOrderID: receiptData?.sOrderID, printerURL, sales_receipt_copies });
    printCopiesToBridge(
      `${printerURL}printer`,
      decodedData,
      { "Content-Type": "text/plain" },
      sales_receipt_copies,
      (i) => {
        console.debug("[CWHCPrint] printerURL post succeeded", { sOrderID: receiptData?.sOrderID, copyIndex: i });
        useTransactionStore.getState().setSelectedPrintTemplateId(null);
        onCopyAttemptDone(true);
      },
      (i, response) => {
        console.error("[CWHCPrint] printerURL post FAILED", { sOrderID: receiptData?.sOrderID, printerURL, copyIndex: i, error: response });
        useTransactionStore.getState().setSelectedPrintTemplateId(null);
        onCopyAttemptDone(false);
      }
    );
  } else if (
    printReciptData.items.length > 0 &&
    !printReciptData.draftFlag &&
    printReciptData.layAway === "Y" &&
    printReciptData.paid === 0 &&
    layaway_receipt_copies !== 0 &&
    !printReciptData?.externalId &&
    !PStatus
  ) {
    // F-11: count attempts (success or failure), not just successes -- see comment on the
    // docTypeTemplateId branch above for why a single failed copy previously blocked this
    // block from ever firing. Requires at least one real success before firing (see same
    // comment for the post-F-11 all-failed regression this guards against).
    let attemptsCompleted = 0;
    let successfulPrints = 0;
    const onCopyAttemptDone = (didSucceed) => {
      attemptsCompleted++;
      if (didSucceed) successfulPrints++;
      if (attemptsCompleted === layaway_receipt_copies) {
        if (successfulPrints === 0) traceGiftCardSkipped("CWHC-copy#4");
        if (successfulPrints === 0) return;
        traceGiftCardLines("CWHC#4", printReciptData);
        printReciptData.items.forEach((data) => {
          if (printReciptData.isReturn && data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Credit Note");
          } else if (data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Gift Card");
          }
        });
      }
    };
    printCopiesToBridge(
      `${printerURL}printer`,
      decodedData,
      { "Content-Type": "text/plain" },
      layaway_receipt_copies,
      () => {
        onCopyAttemptDone(true);
      },
      () => {
        onCopyAttemptDone(false);
      }
    );
  } else if (
    printReciptData.items.length > 0 &&
    !printReciptData.draftFlag &&
    printReciptData.layAway === "Y" &&
    printReciptData.paid !== 0 &&
    layaway_paid_receipt_copies !== 0 &&
    !printReciptData?.externalId &&
    !PStatus
  ) {
    // F-11: count attempts (success or failure), not just successes -- see comment on the
    // docTypeTemplateId branch above for why a single failed copy previously blocked this
    // block from ever firing. Requires at least one real success before firing (see same
    // comment for the post-F-11 all-failed regression this guards against).
    let attemptsCompleted = 0;
    let successfulPrints = 0;
    const onCopyAttemptDone = (didSucceed) => {
      attemptsCompleted++;
      if (didSucceed) successfulPrints++;
      if (attemptsCompleted === layaway_paid_receipt_copies) {
        if (successfulPrints === 0) traceGiftCardSkipped("CWHC-copy#5");
        if (successfulPrints === 0) return;
        traceGiftCardLines("CWHC#5", printReciptData);
        printReciptData.items.forEach((data) => {
          if (printReciptData.isReturn && data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Credit Note");
          } else if (data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Gift Card");
          }
        });
      }
    };
    printCopiesToBridge(
      `${printerURL}printer`,
      decodedData,
      { "Content-Type": "text/plain" },
      layaway_paid_receipt_copies,
      () => {
        onCopyAttemptDone(true);
      },
      () => {
        onCopyAttemptDone(false);
      }
    );
  } else if (
    printReciptData.items.length > 0 &&
    !printReciptData.draftFlag &&
    !PStatus &&
    (printReciptData.layAway === "N" || !printReciptData.layAway) &&
    loyalty_receipt_copies !== 0 &&
    isloyalty
  ) {
    // F-11: count attempts (success or failure), not just successes -- see comment on the
    // docTypeTemplateId branch above for why a single failed copy previously blocked this
    // block from ever firing. Requires at least one real success before firing (see same
    // comment for the post-F-11 all-failed regression this guards against).
    let attemptsCompleted = 0;
    let successfulPrints = 0;
    const onCopyAttemptDone = (didSucceed) => {
      attemptsCompleted++;
      if (didSucceed) successfulPrints++;

      // Execute logic after all copies have been attempted
      if (attemptsCompleted === loyalty_receipt_copies) {
        if (successfulPrints === 0) traceGiftCardSkipped("CWHC-copy#6");
        if (successfulPrints === 0) return;
        if (printReciptData.layAway === "N" && !PStatus && !printReciptData.draftFlag && printReciptData?.payments?.some((payment) => payment?.name === "Cash")) {
          CWOpenDrawer();
        }
        traceGiftCardLines("CWHC#6", printReciptData);
        printReciptData.items.forEach((data) => {
          if (printReciptData.isReturn && data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Credit Note");
          } else if (data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Gift Card");
          }
        });
      }
    };

    // Loop for printing loyalty receipt copies
    printCopiesToBridge(
      `${printerURL}printer`,
      decodedData,
      { "Content-Type": "text/plain" },
      loyalty_receipt_copies,
      () => {
        onCopyAttemptDone(true);
      },
      (i, error) => {
        console.error("Print failed!", error);
        onCopyAttemptDone(false);
      }
    );
  } else {
    console.debug("[CWHCPrint] fell through to fallback (unconditional) print branch", { sOrderID: receiptData?.sOrderID, printerURL, itemsLength: printReciptData.items?.length });
    Axios.post(`${printerURL}printer`, decodedData, {
      headers: {
        "Content-Type": "text/plain",
      },
    })
      .then(() => {
        console.debug("[CWHCPrint] fallback branch printerURL post succeeded", { sOrderID: receiptData?.sOrderID });
        if (
          (printReciptData.layAway === "N" || (printReciptData.layAway === "Y" && printReciptData.paid !== 0)) &&
          !PStatus &&
          !printReciptData.draftFlag &&
          printReciptData?.payments?.some((payment) => payment?.name === "Cash")
        ) {
          CWOpenDrawer();
        }
        traceGiftCardLines("CWHC#7", printReciptData);
        printReciptData.items.forEach((data) => {
          if (printReciptData.isReturn && data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Credit Note");
          } else if (data?.giftCardType === "DIG") {
            GiftCardCWHCPrint(data, "Gift Card");
          }
        });
      })
      .catch((response) => {
        console.error("[CWHCPrint] fallback branch printerURL post FAILED", { sOrderID: receiptData?.sOrderID, printerURL, error: response });
      });
  }
  return processedReceipt;
};

export default CWHCPrint;
